import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Stack, TextField, InputAdornment, Typography } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { GOLD } from 'common/theme/button/premiumButtonSx';

const onlyDigits = (value) => (value || '').replace(/\D/g, '');

const formatCardNumber = (value) =>
  onlyDigits(value).slice(0, 16).replace(/(.{4})/g, '$1 ').trim();

const formatExpiry = (value) => {
  const digits = onlyDigits(value).slice(0, 4);
  return digits.length >= 3 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
};

// Detecta la franquicia por el prefijo del número (Visa/Mastercard/Amex/Diners).
const detectBrand = (digits) => {
  if (/^4/.test(digits)) return { key: 'visa', label: 'VISA', color: '#1A1F71' };
  if (/^(5[1-5]|2[2-7])/.test(digits)) return { key: 'mastercard', label: 'MASTERCARD', color: '#EB001B' };
  if (/^3[47]/.test(digits)) return { key: 'amex', label: 'AMEX', color: '#2E77BC' };
  if (/^3(0[0-5]|[68])/.test(digits)) return { key: 'diners', label: 'DINERS', color: '#0079BE' };
  return null;
};

const computeCard = (numberStr, expiryStr, cvcStr) => {
  const digits = onlyDigits(numberStr);
  const [expMonth, expYear] = expiryStr.split('/');
  const cleanCvc = onlyDigits(cvcStr);
  const numberValid = digits.length >= 15;
  const expiryValid = expMonth?.length === 2 && expYear?.length === 2 && Number(expMonth) >= 1 && Number(expMonth) <= 12;
  const cvcValid = cleanCvc.length >= 3;
  return {
    number: digits,
    exp_month: expMonth || '',
    exp_year: expYear || '',
    cvc: cleanCvc,
    brand: detectBrand(digits)?.key || null,
    numberValid,
    expiryValid,
    cvcValid,
    isValid: numberValid && expiryValid && cvcValid,
  };
};

const fieldSx = {
  '& .MuiFilledInput-root': { background: 'rgba(255,255,255,0.06)', borderRadius: 1.5 },
  '& .MuiFilledInput-root:hover': { background: 'rgba(255,255,255,0.1)' },
  '& .MuiFilledInput-root.Mui-focused': { background: 'rgba(255,255,255,0.1)' },
  '& .MuiFilledInput-input': { color: 'text.primary' },
  // Underline dorado en focus (el de error lo sobreescribe MUI con el color error).
  '& .MuiFilledInput-root:after': { borderBottomColor: GOLD },
  '& .MuiFilledInput-input::placeholder': { color: 'text.disabled', opacity: 1 },
  // Label flotante: más legible en reposo y dorado al enfocar.
  '& .MuiInputLabel-root': { color: 'text.secondary' },
  '& .MuiInputLabel-root.Mui-focused': { color: GOLD },
  // En error solo se marca la CASILLA (underline en rojo); el label se mantiene neutro.
  '& .MuiInputLabel-root.Mui-error': { color: 'text.secondary' },
};

// Marca real de cada franquicia que acepta Wompi (Visa, Mastercard, American Express,
// Diners Club) como un tile estilo tarjeta. Cada una muestra su identidad de marca en
// lugar de un badge de texto genérico.
function BrandMark({ brand }) {
  const svgProps = { width: 38, height: 24, viewBox: '0 0 38 24', role: 'img' };

  switch (brand.key) {
    case 'visa':
      return (
        <svg {...svgProps} aria-label="Visa">
          <rect width="38" height="24" rx="3" fill="#fff" />
          <text x="19" y="16" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontStyle="italic" fontSize="11" letterSpacing="0.5" fill="#1A1F71">VISA</text>
        </svg>
      );
    case 'mastercard':
      return (
        <svg {...svgProps} aria-label="Mastercard">
          <rect width="38" height="24" rx="3" fill="#fff" />
          <circle cx="16" cy="12" r="7" fill="#EB001B" />
          <circle cx="23" cy="12" r="7" fill="#F79E1B" fillOpacity="0.9" />
        </svg>
      );
    case 'amex':
      return (
        <svg {...svgProps} aria-label="American Express">
          <rect width="38" height="24" rx="3" fill="#1F72CD" />
          <text x="19" y="15.5" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="800" fontSize="8.5" letterSpacing="0.3" fill="#fff">AMEX</text>
        </svg>
      );
    case 'diners':
      return (
        <svg {...svgProps} aria-label="Diners Club">
          <rect width="38" height="24" rx="3" fill="#fff" />
          <circle cx="19" cy="12" r="8" fill="#0079BE" />
          <circle cx="19" cy="12" r="3.4" fill="#fff" />
        </svg>
      );
    default:
      return <CreditCardIcon sx={{ color: 'text.disabled' }} />;
  }
}

BrandMark.propTypes = {
  brand: PropTypes.shape({
    key: PropTypes.string.isRequired,
  }).isRequired,
};

/**
 * Campos de tarjeta reutilizables (número con franquicia auto-detectada, vence, CVC).
 * NO incluye "nombre en la tarjeta" (se auto-llena desde la cuenta del usuario).
 * Emite onChange({ number, exp_month, exp_year, cvc, brand, isValid }).
 */
function CardFields({ onChange, disabled = false }) {
  const { t } = useTranslation(['payments']);
  const [number, setNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [touched, setTouched] = useState(false);

  const emit = (n, e, c) => onChange(computeCard(n, e, c));

  const handleNumber = (raw) => {
    const formatted = formatCardNumber(raw);
    setNumber(formatted);
    emit(formatted, expiry, cvc);
  };
  const handleExpiry = (raw) => {
    const formatted = formatExpiry(raw);
    setExpiry(formatted);
    emit(number, formatted, cvc);
  };
  const handleCvc = (raw) => {
    const clean = onlyDigits(raw).slice(0, 4);
    setCvc(clean);
    emit(number, expiry, clean);
  };

  const state = computeCard(number, expiry, cvc);
  const brand = detectBrand(state.number);

  return (
    <Stack spacing={2}>
      <TextField
        label={t('subscription.card.number')}
        value={number}
        onChange={(e) => handleNumber(e.target.value)}
        onBlur={() => setTouched(true)}
        error={touched && !state.numberValid}
        placeholder="1234 5678 9012 3456"
        fullWidth
        variant="filled"
        disabled={disabled}
        sx={fieldSx}
        InputProps={{
          inputProps: { inputMode: 'numeric' },
          endAdornment: (
            <InputAdornment position="end">
              {brand ? (
                <BrandMark brand={brand} />
              ) : (
                <CreditCardIcon sx={{ color: 'text.disabled' }} />
              )}
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" spacing={2}>
        <TextField
          label={t('subscription.card.expiry')}
          value={expiry}
          onChange={(e) => handleExpiry(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && !state.expiryValid}
          placeholder="08/28"
          inputProps={{ inputMode: 'numeric' }}
          fullWidth
          variant="filled"
          disabled={disabled}
          sx={fieldSx}
        />
        <TextField
          label={t('subscription.card.cvc')}
          value={cvc}
          onChange={(e) => handleCvc(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && !state.cvcValid}
          placeholder="123"
          inputProps={{ inputMode: 'numeric' }}
          fullWidth
          variant="filled"
          disabled={disabled}
          sx={fieldSx}
        />
      </Stack>

      {touched && !state.isValid && (
        <Typography variant="caption" sx={{ color: '#ff6b6b' }}>
          {t('subscription.card.invalid')}
        </Typography>
      )}
    </Stack>
  );
}

CardFields.propTypes = {
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default CardFields;
